// ═══════════════════════════════════════════════════════════════════════
// OS SCHEMA DEFINITION
// ═══════════════════════════════════════════════════════════════════════
// This is the load-bearing wall of the OS evolution engine.
// Every deployed OS is not hardcoded React components; it is a JSON object
// adhering to this schema. When the Architect evolves an OS, it modifies
// this schema, and the frontend dynamically re-renders the new shape.
// ═══════════════════════════════════════════════════════════════════════

export interface OSSchema {
  id: string;
  version: string;
  metadata: {
    name: string;
    industry_shape: string;
    brand_color: string;
    created_at: string;
    /**
     * Optional per-OS Operator identity. All fields are optional; defaults
     * fall back to the OS brand for visual identity and "Operator" / a
     * generic tagline for text. Resolve via resolveOperatorIdentity() so
     * older OS rows without this block keep rendering unchanged.
     */
    operator?: {
      name?: string;
      avatar_url?: string;
      avatar_color?: string;
      tagline?: string;
    };
  };
  data_models: Record<string, DataModelDefinition>;
  screens: ScreenTemplate[];
  navigation: NavigationConfig;
}

export interface DataModelDefinition {
  name: string;
  fields: Record<string, FieldDefinition>;
}

export interface FieldDefinition {
  type: "string" | "number" | "boolean" | "date" | "enum" | "reference";
  label: string;
  required?: boolean;
  options?: string[]; // For enums
  references_model?: string; // For relations
}

export interface ScreenNoun {
  singular: string;
  plural: string;
  label: string;
}

export interface ScreenTemplate {
  screen_id: string;
  label: string;
  icon?: string;
  layout:
    | "list"
    | "detail"
    | "kanban"
    | "dashboard"
    | "moodboard"
    | "settings"
    // Phase 5-D — industry-shape-aware third-screen layouts.
    | "portfolio_grid"
    | "gallery_grid"
    | "schedule_grid"
    | "production_board";
  bind_to_model: string | string[]; // Which data model(s) populate this screen
  noun?: ScreenNoun;
  role?: "core" | "primary" | "extra";
  intelligence_prompt_template?: string; // The system prompt injected for the per-screen AI
  components: UIComponent[];
  /**
   * Explicit per-screen field definitions used by <DetailDrawer> to render
   * rows. Optional — when absent, the drawer infers fields from the keys
   * of the first row, so every screen works without a populated schema.
   */
  fields?: ScreenField[];
}

/**
 * Display-level field schema for one column / property of a row on a
 * given screen. Distinct from FieldDefinition (data-model field), which
 * describes the storage type — ScreenField describes how to RENDER and
 * (in 1b/1c) how to EDIT the value.
 */
export interface ScreenField {
  key: string;       // top-level key on the row object (nested paths in a later commit)
  label: string;
  type: "text" | "currency" | "date" | "select" | "user";
  options?: string[]; // values for type="select"
  required?: boolean;
}

export interface UIComponent {
  component_id: string;
  type:
    | "table"
    | "kpi_card"
    | "chart"
    | "form"
    | "image_gallery"
    | "text_block"
    // Phase 5-A — types emitted by buildShapeSkeleton (claim-bundle.ts).
    | "list"
    | "filter"
    | "search"
    | "kanban"
    | "stat_row"
    | "brand_panel"
    | "integration_list"
    // Phase 5-D — grid components for industry-shape-aware layouts.
    | "portfolio_card_grid"
    | "gallery_card_grid"
    | "schedule_block_grid"
    | "production_run_grid"
    // R3 — intelligence outputs from the solution-shape patterns. Ranked
    // alerts + drafted next action (Analyst top-3, Domain Tracker alerts).
    | "urgent_actions"
    // R3 #2 — Qualifier verdict: named buckets (HOT/WARM/COLD/SKIP) with
    // fit + intent scored separately and a drafted next action.
    | "scored_buckets"
    // R3 #3 — Domain Tracker: per-entity development timeline (ordered
    // periods + optional score) with an honest confidence/verification flag.
    | "development_timeline"
    // R3 #4 — Router: roster of specialist cards (name, semantic status
    // dot, one-line coverage). The persistent specialist menu.
    | "specialist_status";
  data_mapping: Record<string, string>; // Maps UI props to DataModel fields
  config?: any; // Component-specific config (e.g. chart type)
}

export interface NavigationConfig {
  items: {
    screen_id: string;
    label: string;
    order: number;
    parent_id?: string;
  }[];
}

const LEADING_NOUN_MODIFIERS = new Set(["all", "my", "the", "existing"]);
const MASS_NOUNS = new Set([
  "machinery",
  "equipment",
  "hardware",
  "software",
  "staff",
  "personnel",
  "infrastructure",
]);

function cleanNounText(raw: string, fallback = "record"): string {
  const cleaned = String(raw || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return cleaned || fallback;
}

export function titleCaseWords(raw: string): string {
  return cleanNounText(raw).replace(/(^|\s)\S/g, (c) => c.toUpperCase());
}

function stripLeadingModifiers(raw: string, fallback = "record"): string {
  const words = cleanNounText(raw, fallback).split(" ").filter(Boolean);
  while (words.length > 1 && LEADING_NOUN_MODIFIERS.has(words[0])) words.shift();
  return words.join(" ") || fallback;
}

function singularizeWord(raw: string): string {
  const word = cleanNounText(raw);
  if (!word || MASS_NOUNS.has(word)) return word || "record";
  if (/ies$/i.test(word)) return word.replace(/ies$/i, "y");
  if (/(ches|shes|sses|xes|zes)$/i.test(word)) return word.replace(/es$/i, "");
  if (/s$/i.test(word) && !/ss$/i.test(word)) return word.slice(0, -1);
  return word;
}

function pluralizeWord(raw: string): string {
  const word = cleanNounText(raw);
  if (!word || MASS_NOUNS.has(word)) return word || "record";
  if (word.endsWith("s") && !word.endsWith("ss")) return word;
  if (word.endsWith("company")) return word.replace(/company$/i, "companies");
  if (word.endsWith("facility")) return word.replace(/facility$/i, "facilities");
  if (word.endsWith("property")) return word.replace(/property$/i, "properties");
  if (word.endsWith("y") && !/[aeiou]y$/i.test(word)) return word.slice(0, -1) + "ies";
  if (/(ch|sh|ss|x|z)$/i.test(word)) return word + "es";
  return word + "s";
}

function mapHeadWord(raw: string, transform: (word: string) => string, fallback = "record"): string {
  const words = stripLeadingModifiers(raw, fallback).split(" ").filter(Boolean);
  if (words.length === 0) return fallback;
  const headIndex = words.length - 1;
  words[headIndex] = transform(words[headIndex]) || fallback;
  return words.join(" ");
}

export function singularizeNoun(raw: string, fallback = "record"): string {
  return mapHeadWord(raw, singularizeWord, fallback);
}

export function pluralizeNoun(raw: string, fallback = "record"): string {
  return mapHeadWord(singularizeNoun(raw, fallback), pluralizeWord, fallback);
}

export function normalizeNoun(
  raw: string,
  options: { fallback?: string; label?: string } = {},
): ScreenNoun {
  const fallback = options.fallback || "record";
  const singular = singularizeNoun(raw, fallback);
  const plural = pluralizeNoun(singular, fallback);
  return {
    singular,
    plural,
    label: options.label?.trim() || titleCaseWords(plural),
  };
}

export const nounForms = normalizeNoun;

export function resolveScreenNoun(
  screen: { noun?: ScreenNoun; bind_to_model?: string | string[]; label?: string } | null | undefined,
  fallback = "record",
): ScreenNoun {
  if (screen?.noun?.singular && screen.noun.plural && screen.noun.label) return screen.noun;
  const bindTo = Array.isArray(screen?.bind_to_model) ? screen.bind_to_model[0] : screen?.bind_to_model;
  if (typeof bindTo === "string" && bindTo.trim()) {
    return normalizeNoun(bindTo, { fallback });
  }
  if (typeof screen?.label === "string" && screen.label.trim()) {
    return normalizeNoun(screen.label.replace(/s$/i, ""), {
      fallback,
      label: screen.label.trim(),
    });
  }
  return normalizeNoun(fallback, { fallback });
}
